import { ApolloLink, FetchResult, Observable, Operation } from '@apollo/client/core';
import { getOperationDefinition } from '@apollo/client/utilities';
import {
    DocumentNode, FragmentDefinitionNode, GraphQLError, SelectionNode,
} from 'graphql';

import {
    CancelBlueSagePayLinkDocument,
    CancelBlueSageTerminalPushDocument,
    CancelUnsentBlueSageTransactionDocument,
    ChargeBlueSageCardDocument,
    CompleteBlueSagePreauthDocument,
    CreateBlueSageClientTokenDocument,
    CreateTransactionDocument,
    DeleteBlueSagePaymentProfileDocument,
    DeleteTransactionByIdDocument,
    GetBlueSageDocument,
    GetBlueSagePaymentProfilesDocument,
    GetBlueSageTerminalPushDocument,
    GetBlueSageTerminalPushStatusDocument,
    GetBlueSageTerminalsDocument,
    JobsV3PageDocument,
    PushBlueSageToTerminalDocument,
    RefundBlueSageTransactionDocument,
    ResendBlueSagePayLinkDocument,
    TransactionStage,
    UpdateTransactionDocument,
    VaultBlueSageCardDocument,
} from 'src/generated/graphql.generated';

import {
    BlueSageMockScenario,
    DEFAULT_MOCK_CARDS,
    DEFAULT_MOCK_LATENCY_MS,
    DEFAULT_MOCK_PUSH,
    DEFAULT_MOCK_TERMINALS,
    MOCK_LOCATION_ID,
    MOCK_PAYMENT_TYPE_ID,
    MockCardOnFile,
    MockPushOutcome,
    activeMockScenario,
    activeMockScenarioName,
    isMockRailAvailable,
} from './bluesage-mock-rail.config';

/** Same origin as this app, so the embedded card form's `postMessage` handshake succeeds. */
const MOCK_EMBED_PATH = '/assets/bluesage-mock/embed.html';

// Requirement: a real client token expires after 15 minutes.
const CLIENT_TOKEN_MINUTES = 15;

const MS_PER_MINUTE = 60_000;
const MS_PER_SECOND = 1000;

// Requirement: spelled exactly as `bluesage.matcher.ts` reads them off a real
// transaction's metadata, since that matcher renders mock and real rows alike.
const META_CHANNEL = 'bluesageChannel';
const META_PAY_URL = 'bluesagePayUrl';
const META_LINK_ID = 'bluesageLinkId';
const META_IDEMPOTENCY_KEY = 'bluesageIdempotencyKey';
const META_LOCATION_ID = 'bluesageLocationId';
const META_PREAUTH_REF = 'bluesagePreauthRef';
const META_TERMINAL_EPI = 'bluesageTerminalEpi';
const META_AMOUNT_PAID = 'bluesageAmountPaid';
const META_TRANSACTION_REF = 'bluesageTransactionRef';
const META_VAULT_CARD = 'bluesageVaultCard';
const META_LINK_CLOSED = 'bluesageLinkClosed';

type Vars = Record<string, any>;

type Outcome =
    | { kind: 'data'; data: Record<string, unknown> }
    | { kind: 'refuse'; message: string }
    | { kind: 'network'; message: string }
    | { kind: 'forward' };

const data = (payload: Record<string, unknown>): Outcome => ({ kind: 'data', data: payload });
const refuse = (message: string): Outcome => ({ kind: 'refuse', message });
const network = (message: string): Outcome => ({ kind: 'network', message });
const forward = (): Outcome => ({ kind: 'forward' });

function resolve(
    result: 'ok' | 'refuse' | 'network' | undefined,
    refusal: string,
    onOk: () => Outcome,
): Outcome {
    if (result === 'refuse') { return refuse(refusal); }
    if (result === 'network') { return network('Failed to fetch'); }
    return onOk();
}

interface MockPush {
    pushId: string;
    transactionId: string;
    epi: string;
    kind: 'sale' | 'auth' | 'ticket';
    pollsLeft: number;
    outcome: MockPushOutcome;
    failure?: string;
    cancelled: boolean;
}

// The jobs reducer replaces a whole row with what a mutation returned, so an
// update that knew only the new stage would blank the row's amount, type and
// invoice on screen. Remembering the create is what keeps the row intact.
interface MockTransaction {
    jobId: string;
    amountMinor: number;
    currency: string;
    type: string;
    invoiceId?: string;
    paymentTypeId?: string;
    metadata: Record<string, unknown>;
}

interface MockState {
    scenario: string | null;
    profiles: MockCardOnFile[];
    pushes: Record<string, MockPush>;
    pushByTransaction: Record<string, string>;
    transactions: Record<string, MockTransaction>;
    // `transactions` above remembers what a create said; this remembers what it
    // answered, which is what gets spliced back into a re-read of the job.
    nodes: Record<string, Record<string, unknown>>;
    invoicePaid: Record<string, number>;
    unknownOutcome: Record<string, true>;
    seq: number;
}

const state: MockState = {
    scenario: null,
    profiles: [],
    pushes: {},
    pushByTransaction: {},
    transactions: {},
    nodes: {},
    invoicePaid: {},
    unknownOutcome: {},
    seq: 0,
};

// Requirement: the harness is deterministic — a scenario's answers must not
// depend on what was done under a previous one, or a run cannot be repeated.
function syncState(scenarioName: string, scenario: BlueSageMockScenario): void {
    if (state.scenario === scenarioName) { return; }

    state.scenario = scenarioName;
    state.profiles = (scenario.cardsOnFile ?? DEFAULT_MOCK_CARDS).map((card) => ({ ...card }));
    state.pushes = {};
    state.pushByTransaction = {};
    state.transactions = {};
    state.nodes = {};
    state.invoicePaid = {};
    state.unknownOutcome = {};
    state.seq = 0;
}

function mockId(prefix: string): string {
    state.seq += 1;
    return `${ prefix }_mock_${ Date.now().toString(36) }${ state.seq }`;
}

// Unix SECONDS — what `freyaDate` and every `createdAt` in this app expect.
function nowSeconds(): number {
    return Math.floor(Date.now() / MS_PER_SECOND);
}

// ── Response builders ───────────────────────────────────────────────────────

// Requirement: the payment type id is the only substitution. It is what routes a
// create to the fake instead of to `transactions.create`, so without it a
// fabricated card payment would be written against a real payment type in Neo4j.
// Everything else is a config read needing no terminal, so it answers for the
// real zone.
function substituteMockPaymentType(result: FetchResult): FetchResult {
    const payload = result.data as Record<string, unknown> | null | undefined;
    const status = payload?.['blueSage'] as Record<string, unknown> | undefined;
    const paymentType = status?.['paymentType'] as Record<string, unknown> | undefined;

    if (!paymentType?.['configId']) { return result; }

    return {
        ...result,
        data: {
            ...payload,
            blueSage: {
                ...status,
                paymentType: { ...paymentType, configId: MOCK_PAYMENT_TYPE_ID },
            },
        },
    };
}

function paymentTypeNode(paymentTypeId: string | undefined): Record<string, unknown> {
    const isBlueSage = paymentTypeId === MOCK_PAYMENT_TYPE_ID;

    return {
        __typename: 'PaymentType',
        id: paymentTypeId ?? null,
        key: isBlueSage ? 'payment-type.bluesage' : 'payment-type.unknown',
        name: isBlueSage ? 'Card (BlueSage)' : 'Unknown payment type',
        provider: isBlueSage ? 'bluesage' : null,
        enabled: true,
    };
}

function buildTerminals(scenario: BlueSageMockScenario): Record<string, unknown> {
    return {
        blueSageTerminals: (scenario.terminals ?? DEFAULT_MOCK_TERMINALS).map((terminal) => ({
            __typename: 'BlueSageTerminal',
            epi: terminal.epi,
            status: terminal.status,
            statusSource: terminal.status === 'unknown' ? 'cache' : 'live',
            statusCheckedAt: new Date().toISOString(),
            busy: terminal.busyJobCode
                ? {
                    __typename: 'BlueSageTerminalBusy',
                    transactionId: `mock-txn-${ terminal.epi }`,
                    jobCode: terminal.busyJobCode,
                    startedAt: nowSeconds(),
                }
                : null,
        })),
    };
}

function profileNode(card: MockCardOnFile): Record<string, unknown> {
    return {
        __typename: 'BlueSagePaymentProfile',
        token: card.token,
        cardBrand: card.cardBrand,
        last4: card.last4,
        createdAt: new Date().toISOString(),
    };
}

function pushNode(push: MockPush | null, settled: boolean, status: string | null) {
    return {
        __typename: 'BlueSageTerminalPush',
        pushId: push?.pushId ?? null,
        transactionRef: push ? `bsp_txn_${ push.pushId }` : null,
        status,
        settled,
    };
}

// `lost` and `none` are both reported as `none`, so the panel's own "give it up
// as lost" counter is what promotes them.
function readPushProgress(push: MockPush): {
    outcome: string;
    settled: boolean;
    status: string | null;
    failure: string | null;
} {
    if (push.cancelled) {
        return { outcome: 'cancelled', settled: true, status: 'cancelled', failure: null };
    }

    if (push.pollsLeft > 0) {
        push.pollsLeft -= 1;
        return { outcome: 'in_flight', settled: false, status: null, failure: null };
    }

    if (push.outcome === 'lost') {
        return { outcome: 'none', settled: false, status: null, failure: null };
    }

    if (push.outcome === 'failed') {
        return {
            outcome: 'failed',
            settled: true,
            status: 'failed',
            failure: push.failure ?? 'That could not be sent to the terminal.',
        };
    }

    return {
        outcome: push.outcome,
        settled: true,
        status: push.outcome,
        failure: null,
    };
}

// Only the fields something actually reads are filled in; the generated type
// selects `Job` scalars nothing on this path touches. Hence the cast at the call
// site.
export function buildMockTransaction(input: {
    transactionId?: string;
    jobId: string;
    amountMinor: number;
    currency?: string;
    stage: string;
    type: string;
    invoiceId?: string;
    paymentTypeId?: string;
    metadata?: Record<string, unknown>;
    // Requirement: the invoice tally must not move on an update — re-adding the
    // amount on a stage change would show the invoice paid twice for one payment,
    // and closing a cancelled row would show it paid at all.
    reusingExisting?: boolean;
}): Record<string, unknown> {
    const id = input.transactionId ?? mockId('txn');
    const currency = input.currency ?? 'USD';
    const metadata = input.metadata ?? {};

    if (input.invoiceId && !input.reusingExisting) {
        state.invoicePaid[input.invoiceId] =
            (state.invoicePaid[input.invoiceId] ?? 0) + input.amountMinor;
    }

    const invoicePaid = input.invoiceId ? (state.invoicePaid[input.invoiceId] ?? 0) : 0;

    state.transactions[id] = {
        jobId: input.jobId,
        amountMinor: input.amountMinor,
        currency,
        type: input.type,
        invoiceId: input.invoiceId,
        paymentTypeId: input.paymentTypeId,
        metadata,
    };

    const node: Record<string, unknown> = {
        __typename: 'Transaction',
        id,
        createdAt: nowSeconds(),
        deletedAt: null,
        archivedAt: null,
        paidAt: input.stage === TransactionStage.Paid ? nowSeconds() : null,
        amount: input.amountMinor,
        currency,
        stage: input.stage,
        type: input.type,
        remarks: null,
        metadata,
        job: { __typename: 'Job', id: input.jobId },
        customer: null,
        invoice: input.invoiceId
            ? {
                __typename: 'Invoice',
                id: input.invoiceId,
                invoiceNumber: 0,
                paidTotal: invoicePaid,
            }
            : null,
        paymentType: paymentTypeNode(input.paymentTypeId),
    };

    state.nodes[id] = node;

    return node;
}

// ── Handlers ────────────────────────────────────────────────────────────────

// Only the card, terminal and pay-link creates need fabricating. A cash/check
// recording carries its own real resolved config id and belongs in the real
// database.
function isBlueSageRail(vars: Vars): boolean {
    return vars.paymentTypeId === MOCK_PAYMENT_TYPE_ID;
}

// In production the pay link is minted INSIDE this mutation, and the ABSENCE of
// the terminal channel marker is what triggers it. That rule is reproduced here
// so the mock mints a link under exactly the condition the server would.
function createTransaction(vars: Vars, scenario: BlueSageMockScenario): Outcome {
    if (!isBlueSageRail(vars)) { return forward(); }

    const metadata: Record<string, unknown> = { ...(vars.metadata ?? {}) };
    const isPayLink = vars.stage === TransactionStage.Pending
        && !metadata[META_CHANNEL];

    if (isPayLink && scenario.payLink === 'refuse') {
        // The real failure shape: `generateBlueSagePayLink` throws, so the whole create
        // fails and no transaction exists afterwards.
        return refuse('That payment could not be started. Nothing was charged.');
    }

    if (isPayLink && scenario.payLink === 'network') {
        return network('Failed to fetch');
    }

    if (isPayLink) {
        const linkId = mockId('bsp_pl');
        metadata[META_LINK_ID] = linkId;
        metadata[META_PAY_URL] = `https://pay.bluesagepay.com/l/${ linkId }`;
        metadata[META_IDEMPOTENCY_KEY] = mockId('idem');
        metadata[META_LOCATION_ID] = MOCK_LOCATION_ID;
    }

    return data({
        createTransaction: {
            __typename: 'CreateTransactionOutput',
            client_secret: null,
            transaction: buildMockTransaction({
                jobId: vars.jobId,
                amountMinor: vars.amount,
                stage: vars.stage,
                type: vars.type,
                invoiceId: vars.invoiceId,
                paymentTypeId: vars.paymentTypeId,
                metadata,
            }),
        },
    });
}

function pushToTerminal(
    vars: Vars,
    scenario: BlueSageMockScenario,
    kind: 'sale' | 'auth' | 'ticket',
): Outcome {
    const behaviour = scenario.push ?? DEFAULT_MOCK_PUSH;

    if (behaviour.accept === 'network') { state.unknownOutcome[vars.transactionId] = true; }

    return resolve(behaviour.accept, behaviour.failure ?? 'That terminal is not available.', () => {
        const push: MockPush = {
            pushId: mockId('bsp_push'),
            transactionId: vars.transactionId,
            epi: vars.epi,
            kind,
            pollsLeft: behaviour.pollsBeforeSettle ?? 0,
            outcome: behaviour.outcome,
            failure: behaviour.failure,
            cancelled: false,
        };

        state.pushes[push.pushId] = push;
        state.pushByTransaction[push.transactionId] = push.pushId;

        // Settles inline only when the scenario asked for no polls, which is the mock
        // middleware's behaviour and not production's. With polls configured this returns
        // unsettled and without a push id, which is what makes the panel follow the push
        // through its transaction as it does in production.
        const inline = push.pollsLeft === 0;
        const settledStatus = inline ? readPushProgress(push) : null;
        const carriesPushId = behaviour.pushIdOnCreate === true || inline;

        const result = {
            __typename: 'BlueSageTerminalPushResult',
            transactionId: push.transactionId,
            stage: settledStatus?.outcome === 'completed' ? 'paid' : 'pending',
            push: pushNode(
                carriesPushId ? push : null,
                Boolean(settledStatus?.settled),
                settledStatus?.status ?? null,
            ),
        };

        return data(kind === 'ticket'
            ? { completeBlueSagePreauth: result }
            : { pushBlueSageToTerminal: result });
    });
}

function readPushById(vars: Vars): Outcome {
    const push = state.pushes[vars.pushId];
    if (!push) { return refuse('No such push.'); }

    const progress = readPushProgress(push);

    return data({
        blueSageTerminalPush: pushNode(push, progress.settled, progress.status),
    });
}

function readPushByTransaction(vars: Vars): Outcome {
    const pushId = state.pushByTransaction[vars.transactionId];
    const push = pushId ? state.pushes[pushId] : undefined;

    if (!push) {
        // `none` is what the server answers; the panel's lost-push counter acts on it.
        return data({
            blueSageTerminalPushStatus: {
                __typename: 'BlueSageTerminalPushStatus',
                transactionId: vars.transactionId,
                stage: 'pending',
                outcome: 'none',
                failure: null,
                startedAt: nowSeconds(),
                push: pushNode(null, false, null),
            },
        });
    }

    const progress = readPushProgress(push);

    return data({
        blueSageTerminalPushStatus: {
            __typename: 'BlueSageTerminalPushStatus',
            transactionId: push.transactionId,
            stage: progress.outcome === 'completed'
                ? (push.kind === 'auth' ? 'pending' : 'paid')
                : (progress.outcome === 'failed' && push.kind !== 'ticket' ? 'cancelled' : 'pending'),
            outcome: progress.outcome,
            failure: progress.failure,
            startedAt: nowSeconds(),
            push: pushNode(push, progress.settled, progress.status),
        },
    });
}

type MockHandler = (vars: Vars, scenario: BlueSageMockScenario) => Outcome;

function operationNameOf(document: DocumentNode): string {
    return getOperationDefinition(document)?.name?.value ?? '';
}

// Requirement: every key is computed from the operation's own generated
// document. An Apollo link matches on the GraphQL operation NAME, which is not
// the generated class name — the BlueSage documents declare `GetBlueSage`, but
// the older platform ones declare `createTransaction` and friends in camelCase.
//
// Requirement: an absence here is a live wire. Anything missing is forwarded to
// the real backend carrying ids this link fabricated, so a new BlueSage or
// transaction operation needs a key here on the same commit.
const JOBS_V3_PAGE = operationNameOf(JobsV3PageDocument);
const GET_BLUESAGE = operationNameOf(GetBlueSageDocument);

const HANDLERS: Record<string, MockHandler> = {

    [operationNameOf(GetBlueSageTerminalsDocument)]: (_vars, scenario) => data(buildTerminals(scenario)),

    [operationNameOf(GetBlueSagePaymentProfilesDocument)]: () => data({
        blueSagePaymentProfiles: state.profiles.map(profileNode),
    }),

    [operationNameOf(CreateBlueSageClientTokenDocument)]: (_vars, scenario) => {
        if (scenario.clientToken === 'refuse') {
            return refuse('The card form could not be opened. Nothing was charged.');
        }
        if (scenario.clientToken === 'network') { return network('Failed to fetch'); }

        // A token with no URL to frame. The dialog has a specific refusal for this, and
        // it is reachable in production.
        const withoutUrl = scenario.clientToken === 'no-embed-url';
        const token = mockId('bsp_ct');

        return data({
            createBlueSageClientToken: {
                __typename: 'BlueSageClientToken',
                embedUrl: withoutUrl
                    ? null
                    : `${ window.location.origin }${ MOCK_EMBED_PATH }?token=${ token }`,
                clientToken: token,
                expiresAt: new Date(
                    Date.now() + (CLIENT_TOKEN_MINUTES * MS_PER_MINUTE),
                ).toISOString(),
                mode: 'mock',
            },
        });
    },

    [operationNameOf(VaultBlueSageCardDocument)]: (vars, scenario) => resolve(
        scenario.charge,
        'That card could not be stored.',
        () => {
            const card: MockCardOnFile = {
                token: mockId('bsp_pp'),
                cardBrand: 'Visa',
                last4: String(vars.cardToken ?? '').slice(-4) || '4242',
            };
            state.profiles = [ ...state.profiles, card ];
            return data({ vaultBlueSageCard: profileNode(card) });
        },
    ),

    [operationNameOf(DeleteBlueSagePaymentProfileDocument)]: (vars) => {
        state.profiles = state.profiles.filter((card) => card.token !== vars.token);
        return data({
            deleteBlueSagePaymentProfile: {
                __typename: 'BlueSagePaymentProfileDeleted',
                deleted: true,
            },
        });
    },

    [operationNameOf(CancelUnsentBlueSageTransactionDocument)]: (vars) => {
        const existing = state.transactions[vars.transactionId];

        if (!existing) {
            return forward();
        }

        const pushId = state.pushByTransaction[vars.transactionId];
        const push = pushId ? state.pushes[pushId] : undefined;
        const sent = Boolean(state.unknownOutcome[vars.transactionId]) || (push && push.outcome !== 'failed');

        return data({
            cancelUnsentBlueSageTransaction: buildMockTransaction({
                transactionId: vars.transactionId,
                jobId: existing.jobId,
                amountMinor: existing.amountMinor,
                currency: existing.currency,
                stage: sent ? TransactionStage.Pending : TransactionStage.Cancelled,
                type: existing.type,
                invoiceId: existing.invoiceId,
                paymentTypeId: existing.paymentTypeId,
                metadata: existing.metadata,
                reusingExisting: true,
            }),
        });
    },

    [operationNameOf(ChargeBlueSageCardDocument)]: (vars, scenario) => {
        if (scenario.charge === 'network') { state.unknownOutcome[vars.transactionId] = true; }

        return resolve(scenario.charge, 'The card was declined. Nothing was charged.', () => {
            // Vaulting is opt-in and the customer's decision, so a card is only added when
            // the dialog asked for it.
            if (vars.vault === true && vars.cardToken) {
                state.profiles = [ ...state.profiles, {
                    token: mockId('bsp_pp'),
                    cardBrand: 'Visa',
                    last4: '4242',
                } ];
            }

            return data({
                chargeBlueSageCard: {
                    __typename: 'BlueSageCardCharge',
                    transactionRef: mockId('bsp_txn'),
                    status: 'approved',
                },
            });
        });
    },

    [operationNameOf(RefundBlueSageTransactionDocument)]: (_vars, scenario) => resolve(
        scenario.refund,
        'That could not be refunded. Nothing was returned to the card.',
        () => data({ refundTransaction: true }),
    ),

    [operationNameOf(PushBlueSageToTerminalDocument)]: (vars, scenario) => pushToTerminal(
        vars, scenario, vars.type === 'auth' ? 'auth' : 'sale',
    ),

    [operationNameOf(CompleteBlueSagePreauthDocument)]: (vars, scenario) => pushToTerminal(vars, scenario, 'ticket'),

    [operationNameOf(GetBlueSageTerminalPushDocument)]: (vars) => readPushById(vars),

    [operationNameOf(GetBlueSageTerminalPushStatusDocument)]: (vars) => readPushByTransaction(vars),

    [operationNameOf(CancelBlueSageTerminalPushDocument)]: (vars) => {
        const push = state.pushes[vars.pushId];
        if (!push) {
            // What the server answers when the device already released it.
            return refuse('That push can no longer be cancelled.');
        }

        push.cancelled = true;
        push.pollsLeft = 0;

        return data({
            cancelBlueSageTerminalPush: {
                __typename: 'BlueSageTerminalPushCancelled',
                status: 'cancelled',
            },
        });
    },

    [operationNameOf(CancelBlueSagePayLinkDocument)]: (vars) => data({
        cancelBlueSagePayLink: {
            __typename: 'BlueSagePayLinkCancelled',
            transactionId: vars.transactionId,
            stage: 'cancelled',
        },
    }),

    [operationNameOf(ResendBlueSagePayLinkDocument)]: (vars, scenario) => {
        const existing = state.transactions[vars.transactionId];

        if (!existing) {
            return refuse('That transaction could not be found in this zone.');
        }
        if (!existing.metadata?.[META_LINK_ID]) {
            return refuse('That transaction has no pay link to replace.');
        }

        if (scenario.payLink === 'refuse') {
            // The server cancels FIRST, so a refusal has already killed the old link.
            existing.metadata = { ...existing.metadata, [META_LINK_CLOSED]: 'cancelled' };
            return refuse(
                'The old pay link was cancelled, but the new one could not be created. '
                + 'The customer can no longer pay the old link — take the payment again from the job.',
            );
        }
        if (scenario.payLink === 'network') { return network('Failed to fetch'); }

        existing.metadata = { ...existing.metadata, [META_LINK_CLOSED]: 'cancelled' };

        const linkId = mockId('bsp_pl');

        return data({
            resendBlueSagePayLink: {
                __typename: 'BlueSagePayLinkResent',
                // A cancelled row never paid, so the invoice tally must not move.
                cancelled: buildMockTransaction({
                    transactionId: vars.transactionId,
                    jobId: existing.jobId,
                    amountMinor: existing.amountMinor,
                    currency: existing.currency,
                    stage: TransactionStage.Cancelled,
                    type: existing.type,
                    invoiceId: existing.invoiceId,
                    paymentTypeId: existing.paymentTypeId,
                    reusingExisting: true,
                    metadata: existing.metadata,
                }),
                created: {
                    __typename: 'CreateTransactionOutput',
                    transaction: buildMockTransaction({
                        jobId: existing.jobId,
                        amountMinor: existing.amountMinor,
                        currency: existing.currency,
                        stage: TransactionStage.Pending,
                        type: existing.type,
                        invoiceId: existing.invoiceId,
                        paymentTypeId: existing.paymentTypeId,
                        // Supersedes rather than collects again, so the tally holds.
                        reusingExisting: true,
                        metadata: {
                            [META_LINK_ID]: linkId,
                            [META_PAY_URL]: `https://pay.bluesagepay.com/l/${ linkId }`,
                            [META_IDEMPOTENCY_KEY]: mockId('idem'),
                            [META_LOCATION_ID]: MOCK_LOCATION_ID,
                            ...(existing.metadata?.[META_VAULT_CARD] === true
                                ? { [META_VAULT_CARD]: true }
                                : {}),
                        },
                    }),
                },
            },
        });
    },

    [operationNameOf(CreateTransactionDocument)]: (vars, scenario) => createTransaction(vars, scenario),

    // Everything but the stage comes from the remembered create, because the jobs
    // reducer REPLACES the row with what this returns — trusting the mutation's own
    // arguments would blank the amount and invoice of the row being closed.
    //
    // Forwarded when the id is not one this link fabricated: that is a real row.
    [operationNameOf(UpdateTransactionDocument)]: (vars) => {
        const existing = state.transactions[vars.transactionId];

        if (!existing) {
            return forward();
        }

        return data({
            updateTransaction: buildMockTransaction({
                transactionId: vars.transactionId,
                jobId: existing.jobId,
                amountMinor: vars.amount ?? existing.amountMinor,
                currency: existing.currency,
                stage: vars.stage ?? TransactionStage.Cancelled,
                type: vars.type ?? existing.type,
                invoiceId: existing.invoiceId,
                paymentTypeId: existing.paymentTypeId,
                metadata: { ...existing.metadata, ...(vars.metadata ?? {}) },
                reusingExisting: true,
            }),
        });
    },

    // Forwarded when the id is not one this link fabricated: that is a real row.
    [operationNameOf(DeleteTransactionByIdDocument)]: (vars) => {
        if (!state.transactions[vars.transactionId]) { return forward(); }

        delete state.nodes[vars.transactionId];

        return data({ deleteTransactions: true });
    },
};

// ── Completing a fabricated result ──────────────────────────────────────────

// Without this, every handler has to reproduce its document's entire selection
// set or Apollo's cache refuses the write with `Missing field '<x>' while writing
// result`. Each handler returns only what something reads, and this fills every
// unmentioned field with null — presence is all the cache wants.
//
// Read from `operation.query` rather than the imported document: Apollo's
// `addTypename` transform has already run by the time a link sees it, so what
// this walks is exactly what will be demanded.
function completeAgainstSelection(
    value: unknown,
    selections: readonly SelectionNode[],
    fragments: Map<string, FragmentDefinitionNode>,
): unknown {
    if (value === null || value === undefined) { return null; }

    if (Array.isArray(value)) {
        return value.map((item) => completeAgainstSelection(item, selections, fragments));
    }

    if (typeof value !== 'object') { return value; }

    const result: Record<string, unknown> = { ...(value as Record<string, unknown>) };

    for (const selection of selections) {
        if (selection.kind === 'Field') {
            const key = (selection.alias ?? selection.name).value;

            // `__typename` is the one field a handler must supply itself: the cache
            // normalises by it, so a null would break identity rather than fill a hole.
            if (key === '__typename') { continue; }

            if (!(key in result)) {
                result[key] = null;
                continue;
            }

            if (selection.selectionSet) {
                result[key] = completeAgainstSelection(
                    result[key], selection.selectionSet.selections, fragments,
                );
            }
            continue;
        }

        // A fragment contributes its fields to the same object, so it is completed
        // against the object in hand and merged back.
        const spread = selection.kind === 'FragmentSpread'
            ? fragments.get(selection.name.value)?.selectionSet.selections
            : selection.selectionSet.selections;

        if (spread) {
            Object.assign(
                result,
                completeAgainstSelection(result, spread, fragments) as Record<string, unknown>,
            );
        }
    }

    return result;
}

function fragmentsOf(query: DocumentNode): Map<string, FragmentDefinitionNode> {
    const fragments = new Map<string, FragmentDefinitionNode>();
    for (const definition of query.definitions) {
        if (definition.kind === 'FragmentDefinition') {
            fragments.set(definition.name.value, definition);
        }
    }

    return fragments;
}

function completeForQuery(
    payload: Record<string, unknown>,
    query: DocumentNode,
): Record<string, unknown> {
    const operation = getOperationDefinition(query);
    if (!operation) { return payload; }

    return completeAgainstSelection(
        payload, operation.selectionSet.selections, fragmentsOf(query),
    ) as Record<string, unknown>;
}

// ── Splicing the fabricated rows into a job read ───────────────────────

// Walking the path beats matching the first field called `transactions`: the
// invoice fragment in the same document has one too, selecting different fields.
function selectionsAtPath(
    selections: readonly SelectionNode[],
    fragments: Map<string, FragmentDefinitionNode>,
    path: readonly string[],
): readonly SelectionNode[] | undefined {
    const [ head, ...rest ] = path;

    for (const selection of selections) {
        if (selection.kind === 'Field') {
            if ((selection.alias ?? selection.name).value !== head) { continue; }
            if (!selection.selectionSet) { return undefined; }

            return rest.length
                ? selectionsAtPath(selection.selectionSet.selections, fragments, rest)
                : selection.selectionSet.selections;
        }

        const spread = selection.kind === 'FragmentSpread'
            ? fragments.get(selection.name.value)?.selectionSet.selections
            : selection.selectionSet.selections;

        const found = spread && selectionsAtPath(spread, fragments, path);
        if (found) { return found; }
    }

    return undefined;
}

/**
 * Put this session's fabricated rows back onto a freshly-read job.
 *
 * The job read is REAL — it is what makes the invoices and the balances worth
 * testing against — and the backend has never heard of a mocked transaction. So
 * every re-read of the job used to wipe them: a settled push schedules a
 * background refresh four seconds later, and the row the operator had just
 * taken vanished off the ledger. An open authorization was therefore impossible
 * to act on here, because Complete and Release are contextual on a row that no
 * longer survived long enough to press them.
 *
 * Splicing happens on the way back from the server and adds no query. A row
 * this link fabricated wins over a real one carrying the same id, which is how
 * a stage change made through the mocked `UpdateTransaction` survives the read
 * that follows it.
 */
function spliceMockTransactions(result: FetchResult, query: DocumentNode): FetchResult {
    const payload = result.data as Record<string, any> | null | undefined;
    const jobs = payload?.jobs?.jobs;

    if (!Array.isArray(jobs) || !jobs.length) { return result; }

    const operation = getOperationDefinition(query);
    if (!operation) { return result; }

    const fragments = fragmentsOf(query);
    const selections = selectionsAtPath(
        operation.selectionSet.selections, fragments, [ 'jobs', 'jobs', 'transactions' ],
    );

    if (!selections) { return result; }

    let spliced = false;

    const patchedJobs = jobs.map((job: Record<string, any>) => {
        const mocked = Object.values(state.nodes)
            .filter((node) => (node['job'] as { id?: string } | null)?.id === job.id);

        if (!mocked.length) { return job; }

        spliced = true;

        const mockedIds = new Set(mocked.map((node) => node['id']));

        return {
            ...job,
            transactions: [
                ...((job['transactions'] ?? []) as Record<string, unknown>[])
                    .filter((row) => !mockedIds.has(row['id'])),
                ...mocked.map((node) => completeAgainstSelection(node, selections, fragments)),
            ],
        };
    });

    if (!spliced) { return result; }

    return {
        ...result,
        data: { ...payload, jobs: { ...payload!['jobs'], jobs: patchedJobs } },
    };
}

// ── The link ────────────────────────────────────────────────────────────────

export function createBlueSageMockRailLink(): ApolloLink {
    return new ApolloLink((operation: Operation, forwardOp) => {
        if (!isMockRailAvailable()) { return forwardOp(operation); }

        const scenarioName = activeMockScenarioName();
        const scenario = activeMockScenario();
        if (!scenarioName || !scenario) { return forwardOp(operation); }

        syncState(scenarioName, scenario);

        // ⚠ Requirement: a zone's own configuration answers for itself, mock
        // rail or not — so the setup status is forwarded and only the payment
        // type's id is swapped. See `substituteMockPaymentType`.
        if (operation.operationName === GET_BLUESAGE) {
            return new Observable<FetchResult>((observer) => {
                const subscription = forwardOp(operation).subscribe({
                    next: (result) => observer.next(substituteMockPaymentType(result)),
                    error: (error) => observer.error(error),
                    complete: () => observer.complete(),
                });

                return () => subscription.unsubscribe();
            });
        }

        // The job read is forwarded and then touched: the real answer is what the screen
        // wants, minus the rows only this link knows about.
        if (operation.operationName === JOBS_V3_PAGE) {
            return new Observable<FetchResult>((observer) => {
                const subscription = forwardOp(operation).subscribe({
                    next: (result) => observer.next(
                        spliceMockTransactions(result, operation.query),
                    ),
                    error: (error) => observer.error(error),
                    complete: () => observer.complete(),
                });

                return () => subscription.unsubscribe();
            });
        }

        const handler = HANDLERS[operation.operationName];
        if (!handler) { return forwardOp(operation); }

        const outcome = handler(operation.variables ?? {}, scenario);
        if (outcome.kind === 'forward') { return forwardOp(operation); }

        const latency = scenario.latencyMs ?? DEFAULT_MOCK_LATENCY_MS;

        return new Observable<FetchResult>((observer) => {
            const timer = setTimeout(() => {
                if (outcome.kind === 'network') {
                    // The outcome is unknown, so the row this call was for stays pending.
                    observer.error(new Error(outcome.message));
                    return;
                }

                if (outcome.kind === 'refuse') {
                    // Apollo turns this into an ApolloError carrying `graphQLErrors` and no
                    // `networkError` — a server refusal.
                    observer.next({
                        data: null,
                        errors: [ new GraphQLError(outcome.message) ],
                    });
                    observer.complete();
                    return;
                }

                observer.next({ data: completeForQuery(outcome.data, operation.query) });
                observer.complete();
            }, latency);

            return () => clearTimeout(timer);
        });
    });
}

/**
 * A pending transaction holding an open authorization, put onto a job.
 *
 * Complete and Release are contextual: they appear only on a job that already
 * carries a `pending` transaction with `bluesagePreauthRef` in its metadata,
 * and nothing in the wizard's own flow can produce one here — the real rails
 * write that marker onto the row after the push settles, and the mock fabricates
 * the row at create time. So the two paths would be untestable without this.
 *
 * Exposed through `blueSageMock.injectHold()`.
 */
export function buildInjectedHold(input: {
    jobId: string;
    amountMinor: number;
    epi?: string;
}): Record<string, unknown> {
    // The scenario's state is otherwise started by the first operation the link
    // answers — which would reset the registry the hold was just written into,
    // and the spliced row would be gone by the next job read.
    const scenarioName = activeMockScenarioName();
    const scenario = activeMockScenario();
    if (scenarioName && scenario) { syncState(scenarioName, scenario); }

    const transaction = buildMockTransaction({
        jobId: input.jobId,
        amountMinor: input.amountMinor,
        stage: TransactionStage.Pending,
        type: 'deposit',
        paymentTypeId: MOCK_PAYMENT_TYPE_ID,
        metadata: {
            [META_CHANNEL]: 'terminal',
            [META_PREAUTH_REF]: mockId('bsp_auth'),
            [META_TERMINAL_EPI]: input.epi ?? '2233445566',
            [META_AMOUNT_PAID]: input.amountMinor,
            [META_TRANSACTION_REF]: mockId('bsp_txn'),
            [META_LOCATION_ID]: MOCK_LOCATION_ID,
        },
    });

    return {
        createTransaction: {
            __typename: 'CreateTransactionOutput',
            client_secret: null,
            transaction,
        },
    };
}
